import { StatusCode } from "api/protocols";
import { DeleteNewsLetterPost, GetNewsLetterPosts, PutNewsLetterPost, PutPublishNewsLetterPost, TNewsLetterPost } from "api/rpc/2024-04/facilityAdmin/facility/newsletter";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Markdown from "components/markdown/Markdown";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import TextEditor from "components/textEditor/textEditor";
import Portal from "elements/Portal";
import { unsavedChangesExist, valueToString } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { showError, showSuccess } from "redux/actions/ui";

interface IState {
    title: string;
    content: string;
    published_at: string;
}

export default function NewsletterPostEdit() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [state, setState] = useState<IState>({
        title: "",
        content: "",
        published_at: "",
    });

    const [beforeChangesState, setBeforeChangesState] = useState<IState>({
        title: "",
        content: "",
        published_at: "",
    });
    const [newsLetterPost, setNewsLetterPost] = useState<TNewsLetterPost>();
    const { id } = useParams<{id: string}>();

    const {state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal} = useModal();
    const {state: publishModal, updateModal: updatePublishModal, closeModal: closePublishModal} = useModal();

    useEffect(() => {
        void getNewsLetterPost();
    }, []);

    async function getNewsLetterPost() {
        const res = await GetNewsLetterPosts({id: Number(id)}, true);
        if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting newsletter posts")));
            return;
        }
        const post = res?.data[0];
        ReactDOM.unstable_batchedUpdates(() => {
            setState({title: post?.title, content: post?.content, published_at: post?.published_at});
            setBeforeChangesState({title: post?.title, content: post?.content, published_at: post?.published_at});
            setNewsLetterPost(post);
        });
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>){
        const {id, value} = e.target;
        setState(prev => ({...prev, [id]: value}));
    }

    async function updatePost() {
        if (!state?.title) {
            dispatch(showError(t("Title field required")));
            return;
        } else if (!state?.content) {
            dispatch(showError(t("Content field required")));
            return;
        }
        
        const res = await PutNewsLetterPost({...state, id: Number(id)}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating post")));
            return;
        }
        dispatch(showSuccess(t("Successfully updated post")));

        const post = res?.data;

        ReactDOM.unstable_batchedUpdates(() => {
            setState({title: post?.title, content: post?.content, published_at: post?.pubished_at});
            setBeforeChangesState({title: post?.title, content: post?.content, published_at: post?.pubished_at});
            setNewsLetterPost(post as TNewsLetterPost);
        });
    }

    async function deletePost() {
        const res = await DeleteNewsLetterPost({id: Number(id)}, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting post")));
            return;
        }
        dispatch(showSuccess(t("Successfully deleted post")));
        history.push("/admin/newsletter");
    }

    async function publishPost() {
        const res = await PutPublishNewsLetterPost({id: Number(id)}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error publishing post")));
            return;
        }
        void closePublishModal();
       
        dispatch(showSuccess(t("Successfully published post")));
        const post = res?.data;
        ReactDOM.unstable_batchedUpdates(() => {
            setState({title: post?.title, content: post?.content, published_at: post?.pubished_at});
            setBeforeChangesState({title: post?.title, content: post?.content, published_at: post?.pubished_at});
            setNewsLetterPost(post as TNewsLetterPost);
        });
    }

    return (
    <Page
    title={t("Edit Newsletter Post")}
    breadcrumbs={[{prefix: true, label: t("Back"), url: "/admin/newsletter"}]}
    primaryAction={{content: t("Save"), action: () => updatePost(), disabled: newsLetterPost?.status === "published"}}
    subtitle={newsLetterPost?.published_at && newsLetterPost?.status === "published" ? t("Published on {{publishDate}} by {{publishUserName}}", {publishDate: moment?.utc((newsLetterPost?.published_at))?.local()?.format("LLL"), publishUserName: newsLetterPost?.published_by_name}) : undefined}
    titleMetadata={<Badge size="medium" type={newsLetterPost?.status === "draft" ? "warning" : newsLetterPost?.status === "published" ? "success" : "gray"}>{valueToString(newsLetterPost?.status)}</Badge>}
    multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Publish"),
              icon: "circle-check",
              disabled: newsLetterPost?.status !== "draft",
              handler: () => updatePublishModal({isOpen: true}),
            },
            {
              type: "handler",
              label: t("Delete"),
              icon: "trash",
              handler: () => updateDeleteModal({isOpen: true}),
            },
          ],
        },
      }}
    notificationBarProps={{
        isVisible: unsavedChangesExist(state, beforeChangesState),
        onAction: () => updatePost(),
        onCancel: () => setState({...beforeChangesState}),
      }}
    >
     <Card>
        <Card.Section>
            <FormLayout>
                <FormLayout.Group>
                    <Input label={t("Title")} placeholder={t("Title")} onChange={handleInputChange} value={state?.title} id={"title"} disabled={newsLetterPost?.status === "published"}/>
                </FormLayout.Group>
                <FormLayout.Group alignStretch>
                    <TextEditor label={t("Content")} markdownText={state?.content} markdownTextOnChange={value => setState(prev => ({...prev, content: value}))} readOnly={newsLetterPost?.status === "published"}/>
                    <div style={{height: "100%"}}>
                    <span className="ui-email-template-preview-label">{t("Preview")}</span>
                        <div className="ui-email-template-preview-email" style={{padding: "24px"}}>
                            <div style={{width: "70%", backgroundColor: "#FFFFFF", padding: "32px", borderRadius: "12px"}}>
                            <div className="flex flex-col gap-1">
                            <p className="newsletter-posts-publish-date">{state?.published_at ? moment(state?.published_at, "YYYY-MM-DD HH:mm:ss")?.format("ll") : moment()?.format("ll")}</p>
                            <h1 className="newsletter-posts-header-text">{state?.title}</h1>
                            <p className="newsletter-posts-content newsletter-posts-content-expanded"><Markdown markdownText={state?.content}/></p>
                            </div>
                            </div>
                        </div>
                    </div>
                </FormLayout.Group>
            </FormLayout>
        </Card.Section>
    </Card> 


    <Portal isMounted={deleteModal?.isOpen}>
        <Popup
            open={deleteModal?.isOpen}
            title={t("Delete Post")} 
            type={"warning"} 
            description={t("Are you sure you want to delete this post?")} 
            okText={t("Delete")}      
            onCancel={closeDeleteModal}
            onOk={deletePost}
            />
    </Portal>  

    <Portal isMounted={publishModal?.isOpen}>
        <Popup
            open={publishModal?.isOpen}
            title={t("Publish Post")} 
            type={"info"} 
            description={t("Are you sure you want to publish this post? In addition to this post being publicly viewable by customers, the customers subscribed to the newsletter will be emailed this post")} 
            okText={t("Post")}      
            onCancel={closePublishModal}
            onOk={publishPost}
            />
    </Portal>  
      
    </Page>  
    );
}